import { useMemo, useState, type CSSProperties, type FC } from 'react';
import type { EditorObject, EditorTabKey } from '../types';
import { CloseIcon, SearchIcon } from './EditorIcons';

interface EditorWorkCenterOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  objects: EditorObject[];
  initialTab?: EditorTabKey;
  onSelectObject: (obj: EditorObject, sourceTab: EditorTabKey) => void;
}

function statusAccent(status: string): string {
  switch (status) {
    case 'На проверке':
    case 'IN_REVIEW':
      return '#e2ad3a';
    case 'Нужен ответ':
    case 'NEEDS_RESPONSE':
      return '#ff7b65';
    case 'Активен':
    case 'ACTIVE':
    case 'В работе':
      return '#41d2ff';
    case 'На рассмотрении':
    case 'CONSIDERATION':
      return '#9e80ff';
    case 'Ожидает решения':
    case 'PENDING_APPROVAL':
      return '#e2ad3a';
    case 'Завершено':
    case 'DONE':
      return '#49ddb7';
    default:
      return '#8faebb';
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'ARTICLE':
      return 'Статья';
    case 'COAUTHOR':
      return 'Соавторство';
    case 'EDITORIAL':
      return 'Редакционный проект';
    case 'MATERIAL':
      return 'Материалы';
    case 'APPROVAL':
      return 'Согласование';
    case 'DONE':
      return 'Завершённые';
    default:
      return kind;
  }
}

/** Work center — cab-overlay chrome + cabinet-* body. */
export const EditorWorkCenterOverlay: FC<EditorWorkCenterOverlayProps> = ({
  isOpen,
  onClose,
  objects,
  initialTab = 'ALL',
  onSelectObject,
}) => {
  const [activeTab, setActiveTab] = useState<EditorTabKey>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const tabCounts = useMemo(
    () => ({
      ALL: objects.length,
      ARTICLE: objects.filter((o) => o.kind === 'ARTICLE').length,
      COAUTHOR: objects.filter((o) => o.kind === 'COAUTHOR').length,
      EDITORIAL: objects.filter((o) => o.kind === 'EDITORIAL').length,
      MATERIAL: objects.filter((o) => o.kind === 'MATERIAL').length,
      APPROVAL: objects.filter((o) => o.kind === 'APPROVAL').length,
      DONE: objects.filter((o) => o.kind === 'DONE' || o.is_archived).length,
    }),
    [objects],
  );

  const filteredObjects = useMemo(() => {
    return objects.filter((item) => {
      if (activeTab !== 'ALL') {
        if (activeTab === 'DONE') {
          if (item.kind !== 'DONE' && !item.is_archived && item.status !== 'Завершено') {
            return false;
          }
        } else if (item.kind !== activeTab) {
          return false;
        }
      }

      if (statusFilter !== 'ALL' && item.status !== statusFilter) {
        return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = item.title.toLowerCase().includes(query);
        const matchesCode = item.object_code.toLowerCase().includes(query);
        const matchesAuthor = item.author_name?.toLowerCase().includes(query) ?? false;
        const matchesStep = item.next_step?.toLowerCase().includes(query) ?? false;
        if (!matchesTitle && !matchesCode && !matchesAuthor && !matchesStep) {
          return false;
        }
      }

      return true;
    });
  }, [objects, activeTab, statusFilter, searchQuery]);

  if (!isOpen) return null;

  const tabs: Array<{ key: EditorTabKey; label: string; count: number }> = [
    { key: 'ALL', label: 'Все', count: tabCounts.ALL },
    { key: 'ARTICLE', label: 'Статья', count: tabCounts.ARTICLE },
    { key: 'COAUTHOR', label: 'Соавторство', count: tabCounts.COAUTHOR },
    { key: 'EDITORIAL', label: 'Редакционный проект', count: tabCounts.EDITORIAL },
    { key: 'MATERIAL', label: 'Материалы', count: tabCounts.MATERIAL },
    { key: 'APPROVAL', label: 'Согласование', count: tabCounts.APPROVAL },
    { key: 'DONE', label: 'Завершённые', count: tabCounts.DONE },
  ];

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Редактор проекта</small>
              <h2>Мои заявки</h2>
              <p>Единый рабочий центр редакционных объектов</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cabinet-toolbar" style={{ padding: '12px 18px' }}>
          <div className="cabinet-filters">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`cabinet-filter${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label} · {tab.count}
              </button>
            ))}
          </div>
          <div className="cabinet-actions" style={{ gap: 8 }}>
            <div style={{ position: 'relative', minWidth: 200 }}>
              <SearchIcon
                className="w-4 h-4"
                style={{ position: 'absolute', left: 10, top: 12, opacity: 0.55 }}
              />
              <input
                type="search"
                className="cabinet-search"
                style={{ width: '100%', paddingLeft: 32 }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по ID, названию или автору…"
              />
            </div>
            <select
              className="cabinet-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">Все статусы</option>
              <option value="На проверке">На проверке</option>
              <option value="Нужен ответ">Нужен ответ</option>
              <option value="Активен">Активен</option>
              <option value="На рассмотрении">На рассмотрении</option>
              <option value="Ожидает решения">Ожидает решения</option>
              <option value="Завершено">Завершено</option>
            </select>
          </div>
        </div>

        <div className="cab-body">
          <section className="cabinet-hero" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
            <div>
              <div className="cabinet-eyebrow">Редакционный реестр</div>
              <h2>Мои заявки</h2>
              <p>
                Все редакционные направления собраны в одном рабочем центре: статьи, соавторство,
                проекты, материалы и согласования.
              </p>
            </div>
            <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              Роль: Редактор проекта
            </span>
          </section>

          {filteredObjects.length === 0 ? (
            <div className="cabinet-note" style={{ marginTop: 14 }}>
              Объекты по выбранному фильтру не найдены.
            </div>
          ) : (
            <div className="cabinet-grid2" style={{ marginTop: 14 }}>
              {filteredObjects.map((obj) => (
                <article
                  key={obj.id}
                  className="cabinet-card"
                  style={{ '--accent': statusAccent(obj.status), cursor: 'pointer' } as CSSProperties}
                  onClick={() => onSelectObject(obj, activeTab)}
                >
                  <div className="cabinet-actions">
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': '#41d2ff' } as CSSProperties}
                    >
                      {obj.object_code} · {kindLabel(obj.kind)}
                    </span>
                    <span className="cabinet-spacer" />
                    <span style={{ fontSize: 11, color: '#8faebb' }}>v{obj.current_version}</span>
                  </div>
                  <h3 style={{ margin: '10px 0 4px', fontSize: 15 }}>{obj.title}</h3>
                  <p style={{ margin: 0, color: '#8faebb', fontSize: 12 }}>
                    {obj.author_name || obj.editorial_team || 'Редакционная коллегия'}
                  </p>
                  <div className="cabinet-grid2" style={{ marginTop: 12, gap: 8 }}>
                    <div className="cabinet-note">
                      <span style={{ fontSize: 10, color: '#8faebb' }}>Статус</span>
                      <div style={{ marginTop: 4 }}>
                        <span
                          className="cabinet-badge"
                          style={{ '--badge': statusAccent(obj.status) } as CSSProperties}
                        >
                          {obj.status}
                        </span>
                      </div>
                    </div>
                    <div className="cabinet-note">
                      <span style={{ fontSize: 10, color: '#8faebb' }}>Следующий шаг</span>
                      <strong style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                        {obj.next_step || 'Проверить поступившие материалы'}
                      </strong>
                    </div>
                  </div>
                  <div className="cabinet-actions" style={{ marginTop: 12 }}>
                    <span style={{ fontSize: 11, color: '#8faebb' }}>
                      {obj.journal_target || 'Zertteu Journal Core'}
                    </span>
                    <span className="cabinet-spacer" />
                    <button
                      type="button"
                      className="cabinet-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectObject(obj, activeTab);
                      }}
                    >
                      Открыть
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
